import { IActionDefinitionDto, IGenericSelectDataModel, IWorkFlowBaseInfo } from "@kuika/kuika-cl-model";
import _ from "lodash";
import ObjectHash from "object-hash";
import * as React from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { ActionUsagesButton } from "../../../common/action-usages-button";
import { ActionUsedInProcessAlert } from "../../../common/action-used-in-process-alert";
import CloseConfirmationModal from "../../../common/close-confirmation-modal";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSpinnerSmall from "../../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import { GenericSqlPanelComponent } from "./generic-sql-panel";
import "./generic-sql-modal.scss";

/** The gutter the SQL action editor leaves on either side; this sheet is the same size. */
const MODAL_MARGIN: number = 40;

export interface IGenericSqlModalProps {
  showModal: boolean;
  closeModal: () => void;
  datasourceId: string;
  nameValidator: (rule, value) => void;
  genericSelectData: IGenericSelectDataModel | undefined;
  updateGenericSelectData: (data: IGenericSelectDataModel) => void;
  convertToSql: (sqlString: string) => void;
  /** The save. Resolve `true` when it succeeded: the dialog then shows its ✓ and calls `onSaved`. */
  onSubmit: () => Promise<boolean | void> | void;
  /**
   * The save succeeded and its ✓ has been seen: the owner closes the dialog here. Without it the
   * owner is expected to close the dialog itself inside `onSubmit`, as before.
   */
  onSaved?: () => void;
  actionToEdit?: IActionDefinitionDto;
  actionName?: string;
  onActionNameChange?: (name: string) => void;
  startEditingName?: boolean;
  nameHasError?: boolean;
  workFlowList?: IWorkFlowBaseInfo[];
  projectId?: string;
}

export interface IGenericSqlModalState {
  isCreating: boolean;
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  showUnsavedChangesModal: boolean;
  nameHasError: boolean;
}

export class GenericSqlModal extends React.PureComponent<IGenericSqlModalProps, IGenericSqlModalState> {
  constructor(props: IGenericSqlModalProps) {
    super(props);
    this.state = {
      isCreating: false,
      saveStatus: "idle",
      showUnsavedChangesModal: false,
      nameHasError: false
    };
  }

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  initialDataHash: string = "";
  initialSelectData: IGenericSelectDataModel | undefined = undefined;
  initialActionName: string | undefined = undefined;

  componentDidMount() {
    if (this.props.showModal) {
      this.captureInitialState();
    }
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidUpdate(prevProps: IGenericSqlModalProps) {
    if (this.props.showModal && !prevProps.showModal) {
      this.captureInitialState();
      this.setState({ isCreating: false });
    }
  }

  captureInitialState = () => {
    const { genericSelectData, actionName } = this.props;
    this.initialSelectData = _.cloneDeep(genericSelectData);
    this.initialActionName = actionName;
    this.initialDataHash = ObjectHash({ genericSelectData, actionName });
  };

  handleClose = () => {
    const { genericSelectData, actionName } = this.props;
    const currentHash = ObjectHash({ genericSelectData, actionName });

    if (this.initialDataHash !== currentHash) {
      this.setState({ showUnsavedChangesModal: true });
    } else {
      this.discardChanges();
    }
  };

  discardChanges = () => {
    if (this.initialSelectData !== undefined) {
      this.props.updateGenericSelectData(this.initialSelectData);
    } else if (this.props.genericSelectData !== undefined) {
      this.props.updateGenericSelectData(undefined as any);
    }

    if (this.initialActionName !== undefined && this.props.onActionNameChange) {
      this.props.onActionNameChange(this.initialActionName);
    } else if (this.props.onActionNameChange) {
      this.props.onActionNameChange("");
    }

    this.setState({ showUnsavedChangesModal: false });
    this.props.closeModal();
  };
  handleClickCreate = async (): Promise<void> => {
    if (!this.props.actionName?.trim()) {
      this.setState({ nameHasError: true });
      KMainFunctions.displayErrorNotification("Please enter an action name.");
      return;
    }
    if (!this.props.genericSelectData?.tables || this.props.genericSelectData.tables?.length === 0) {
      KMainFunctions.displayWarningNotification("Please select at least one source.");
      return;
    }
    if (this.props.genericSelectData?.hasValidationError) {
      KMainFunctions.displayErrorNotification("Please fix validation errors.");
      return;
    }
    // The commit is live again as soon as the ✓ replaces the spinner, so a second press would
    // re-send the save behind the closing dialog.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    this.setState({ isCreating: true, nameHasError: false });
    let isSaved: boolean = false;
    try {
      const result: boolean | void = await this.props.onSubmit();
      isSaved = result === true;
    } catch (error: unknown) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      // Clear the dimming either way: on a failure so the user can retry instead of being stuck
      // (KOA-17354), on a success so the ✓ is not read through it.
      if (this.props.showModal) {
        this.setState({ isCreating: false });
      }
    }
    if (!isSaved) {
      this.saveStatus.fail();
      return;
    }
    // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
    this.saveStatus.succeed(this.props.onSaved);
  };

  render() {
    const {
      showModal,
      closeModal,
      datasourceId,
      nameValidator,
      genericSelectData,
      updateGenericSelectData,
      convertToSql,
      onSubmit,
      actionToEdit,
      actionName,
      onActionNameChange
    } = this.props;

    const footer = (
      <KModalFooter
        onCancel={this.handleClose}
        cancelTestId="genericSqlModalCancelButton"
        submitLabel={!actionToEdit || !actionToEdit?.id ? "Create" : "Update"}
        submitTestId="genericSqlModalSubmitButton"
        onSubmit={this.handleClickCreate}
        status={this.state.saveStatus}
      />
    );

    return (
      <>
        <CloseConfirmationModal
          closeType="Modal"
          triggerModal={this.state.showUnsavedChangesModal}
          closeWithoutSaveHandler={this.discardChanges}
          updateAndCloseHandler={() => {
            this.setState({ showUnsavedChangesModal: false });
            this.handleClickCreate();
          }}
          cancel={() => this.setState({ showUnsavedChangesModal: false })}
        />
        <KModal
          showModal={showModal}
          closeModal={this.handleClose}
          hideHeader={true}
          width={`calc(100vw - ${MODAL_MARGIN * 2}px)`}
          centered
          bodyStyle={{ padding: 0 }}
          className="generic-sql-modal"
        >
          <KuikaSpinnerSmall spinning={this.state.isCreating}>
            <ActionUsedInProcessAlert
              actionId={actionToEdit?.id}
              workFlowList={this.props.workFlowList}
              projectId={this.props.projectId}
            />
            <ActionUsagesButton actionId={actionToEdit?.id} actionName={actionToEdit?.name} />
            <GenericSqlPanelComponent
              isModal={true}
              datasourceId={datasourceId}
              nameValidator={nameValidator}
              genericSelectData={genericSelectData}
              updateGenericSelectData={updateGenericSelectData}
              convertToSql={convertToSql}
              actionName={actionName}
              onActionNameChange={(name) => {
                this.setState({ nameHasError: !name?.trim() });
                onActionNameChange?.(name);
              }}
              footer={footer}
              startEditingName={this.props.startEditingName}
              nameHasError={this.state.nameHasError}
            />
          </KuikaSpinnerSmall>
        </KModal>
      </>
    );
  }
}
